import type { InviteEmailStatus, InviteStatus } from '@server/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, LogOut, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { GatewayBrand, GatewayContent, GatewayShell, GatewaySpinner } from '@/_shared/components/GatewayShell';
import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { useAuth } from '@/_shared/hooks/useAuth';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useRunOnceWhen } from '@/_shared/hooks/useRunOnceWhen';
import { acceptInvite } from '@/_shared/lib/accept-invite';
import { orpc } from '@/_shared/lib/orpc';

import { SignInErrorCallout } from '../components/SignInErrorCallout';
import { SignInProviders } from '../components/SignInProviders';
import { useSignInError } from '../hooks/useSignInError';

function InviteShell({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <GatewayShell>
      <GatewayBrand poweredBy />
      {children}
    </GatewayShell>
  );
}

// Client-facing counterpart to the backend's INVITE_BLOCK_MESSAGES (src/services/membership.ts):
// the reason shown on the "Invite Unavailable" card for each non-pending status.
const INVITE_UNAVAILABLE_MESSAGES: Record<Exclude<InviteStatus, 'pending'>, string> = {
  expired: 'This invitation has expired. Ask the workspace admin for a new one.',
  revoked: 'This invitation has been revoked.',
  accepted: 'This invitation has already been accepted.',
  invalid: 'This invitation is no longer valid.',
};

// Message and button label live together so an edit to one cannot leave the other behind.
const EMAIL_BLOCKED_COPY: Record<Exclude<InviteEmailStatus, 'ok'>, { message: string; action: string }> = {
  wrong_account: {
    message: 'You are signed in with a different account. Sign out and sign in with the invited email to continue.',
    action: 'Sign out and switch account',
  },
  unproven: {
    message:
      'Your account carries the invited email, but we could not confirm it with RudderStack. Sign out and sign in again. If the same screen returns, the check is still unavailable — contact support.',
    action: 'Sign out and sign in again',
  },
};

/** The page that the invitee opened before sign-in, when it belongs to the workspace they joined. */
function pageInWorkspace(page: string | null, workspaceId: string): string | null {
  const home = `/w/${workspaceId}`;
  if (!page?.startsWith(home)) return null;
  const rest = page.slice(home.length);
  return rest === '' || /^[/?#]/.test(rest) ? page : null;
}

export function AcceptInvite(): React.ReactElement {
  usePageTitle('Accept Invite');
  const { inviteId } = useParams<{ inviteId: string }>();
  const [searchParams] = useSearchParams();
  // The error of a Google or SSO sign-in that started on this page.
  const signInError = useSignInError();
  // That sign-in used an email with no pending invite, so Lookout stopped it (ACT2-871).
  const signInMismatch = signInError.code === 'invite_unavailable';
  const pageAfterJoin = searchParams.get('return_to');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading: authLoading, signOutAndRedirect } = useAuth();
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);

  const { data, isLoading, error } = useQuery(
    orpc.workspace.getInviteInfo.queryOptions({
      input: { inviteId: inviteId! },
    }),
  );

  const returnTo = inviteId ? `/invite/${inviteId}` : null;

  useEffect(() => {
    if (!user && inviteId) {
      sessionStorage.setItem('pending_invite', inviteId);
    }
  }, [user, inviteId]);

  const inviteTerminal = data != null && data.status !== 'pending';
  useEffect(() => {
    if (error || inviteTerminal) {
      sessionStorage.removeItem('pending_invite');
    }
  }, [error, inviteTerminal]);

  const handleAccept = useCallback(async () => {
    if (isAccepting || !inviteId) return;
    setIsAccepting(true);
    setAcceptError(null);
    try {
      const workspace = await acceptInvite(inviteId);

      sessionStorage.removeItem('pending_invite');
      // The workspace route checks membership in auth.me. Load the new membership first, or the route sends
      // the user to the home resolver, which opens /new-workspace for a user with no other workspace.
      await queryClient.refetchQueries({ queryKey: orpc.auth.me.key() });
      const target = pageInWorkspace(pageAfterJoin, workspace.id) ?? `/w/${workspace.id}`;
      void navigate(target, { replace: true });
      void queryClient.invalidateQueries();
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'Failed to accept invite');
      setIsAccepting(false);
    }
  }, [inviteId, isAccepting, pageAfterJoin, queryClient, navigate]);

  // Auto-accept for an already-member fires at most once (the one-shot guard stops the retry-forever
  // loop a failed accept would otherwise cause as handleAccept's identity churns on isAccepting).
  useRunOnceWhen(Boolean(data?.alreadyMember && user), () => void handleAccept());

  const handleSignOut = useCallback(async () => {
    // Reuse the shared fail-closed logout (AI-1030) so this path also revokes the session
    // server-side and clears the last-workspace hint — instead of re-implementing
    // it. Only reload into the logged-out state once logout succeeds; on failure it already
    // surfaces a toast (the app-root <Toaster/> covers this route) and keeps the still-valid cookie,
    // so we just stay put. A Google or SSO session goes through its provider's sign-out page instead.
    await signOutAndRedirect(returnTo ?? '/login');
  }, [returnTo, signOutAndRedirect]);

  if (authLoading || isLoading) {
    return (
      <InviteShell>
        <GatewaySpinner mode="inline" label="Preparing invite…" />
      </InviteShell>
    );
  }

  if (error || !data) {
    return (
      <InviteShell>
        <GatewayContent
          icon={<CircleAlert />}
          variant="error"
          title="Invite Not Found"
          description="This invitation may be invalid or has been removed."
        />
      </InviteShell>
    );
  }

  if (data.alreadyMember && user) {
    if (acceptError) {
      return (
        <InviteShell>
          <GatewayContent icon={<CircleAlert />} variant="error" title="Unable to Join" description={acceptError} />
        </InviteShell>
      );
    }

    return (
      <InviteShell>
        <GatewaySpinner mode="inline" label="Joining workspace…" />
      </InviteShell>
    );
  }

  // Any non-pending status is terminal (matches inviteTerminal above); the server resolves the
  // precedence, so a revoked+expired invite shows one consistent reason on both surfaces.
  if (data.status !== 'pending') {
    return (
      <InviteShell>
        <GatewayContent
          icon={<CircleAlert />}
          variant="warning"
          title="Invite Unavailable"
          description={INVITE_UNAVAILABLE_MESSAGES[data.status]}
        />
      </InviteShell>
    );
  }

  const inviterAttribution =
    'inviterDisplayName' in data && typeof data.inviterDisplayName === 'string' ? (
      <div className="flex flex-col items-center gap-1.5 text-sm text-muted-foreground">
        <span>Invited by</span>
        {/* The avatar and the name share one line, so a long email cannot wrap under the avatar. */}
        <span className="flex max-w-full items-center gap-2">
          <Avatar className="size-5 shrink-0">
            <AvatarImage src={data.inviterAvatarUrl ?? undefined} alt={data.inviterDisplayName} />
            <AvatarFallback>{data.inviterDisplayName[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <TruncateWithTooltip className="font-medium text-foreground">{data.inviterDisplayName}</TruncateWithTooltip>
        </span>
      </div>
    ) : undefined;

  return (
    <InviteShell>
      <GatewayContent
        icon={<Users />}
        variant="brand"
        title={
          <>
            {"You've been invited to join the "}
            {/* nowrap: a hyphen is a break opportunity, so the token would otherwise split in two. */}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em] whitespace-nowrap">
              {data.workspaceName}
            </code>
            {' workspace'}
          </>
        }
        description={inviterAttribution}
      >
        <div className="flex flex-col gap-4">
          {/* A signed-in invitee with the invited email has nothing to switch to. */}
          {data.maskedEmail && (!user || data.emailStatus !== 'ok') ? (
            <p className="text-sm text-muted-foreground">
              Sign in as <span className="font-medium text-foreground">{data.maskedEmail}</span> to accept.
            </p>
          ) : null}

          {acceptError ? (
            <Callout variant="error" role="alert" className="text-left">
              {acceptError}
            </Callout>
          ) : null}

          {!user && signInError.message ? (
            <SignInErrorCallout
              message={
                signInMismatch
                  ? 'The account you used does not match this invite. Sign in with the invited email.'
                  : signInError.message
              }
            />
          ) : null}

          {user && data.emailStatus !== 'ok' ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">{EMAIL_BLOCKED_COPY[data.emailStatus].message}</p>
              <Button
                variant="secondary"
                size="lg"
                className="w-full gap-2"
                Icon={<LogOut />}
                onClick={() => void handleSignOut()}
              >
                {EMAIL_BLOCKED_COPY[data.emailStatus].action}
              </Button>
            </div>
          ) : user ? (
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={() => void handleAccept()}
              loading={isAccepting}
            >
              {isAccepting ? 'Joining…' : 'Join the workspace'}
            </Button>
          ) : (
            <SignInProviders returnTo={returnTo} invite page="invite" onNewAttempt={signInError.dismiss} />
          )}
        </div>
      </GatewayContent>
    </InviteShell>
  );
}
